import { ReactNode } from "react";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import {
  Form,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormRow,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { InfoBulleButton } from "@src/components/infoBulleButton/InfoBulleButton";
import { InfoBulle } from "@src/components/infoBulle/InfoBulle";
import { NavButton } from "@src/components/navButton/NavButton";
import { UseFormReturn } from "@lya-protect/lya-protect-form-library/dist/Exports/reactHookForm.exports";
import { PopperButton } from "@src/PopperButton";
import ACTIVITES from "./activitiesConfig";
import { StepProps } from "../../step/Steps";
import "./activities.scss";

export type ActivitiesData = {
  sports: boolean;
  cycling: boolean;
  hiking: boolean;
  cooking: boolean;
  gardening: boolean;
  handiwork: boolean;
  outings: boolean;
};

type ActivitiesProps = StepProps<ActivitiesData>;

const schema = zod.object({
  sports: zod.boolean().optional().default(false),
  cycling: zod.boolean().optional().default(false),
  hiking: zod.boolean().optional().default(false),
  cooking: zod.boolean().optional().default(false),
  gardening: zod.boolean().optional().default(false),
  handiwork: zod.boolean().optional().default(false),
  outings: zod.boolean().optional().default(false),
});
type FormValues = zod.infer<typeof schema>;

export type _Activity = {
  label: string;
  name:
    | "sports"
    | "cycling"
    | "hiking"
    | "cooking"
    | "gardening"
    | "handiwork"
    | "outings";
  cover: string;
  modal: {
    accident: ReactNode;
    blessure: ReactNode;
    indemnisation: ReactNode;
  };
};

type FakeCheckboxProps = {
  formConfig: UseFormReturn<FormValues>;
  activity: _Activity;
};
function FakeCheckbox({ formConfig, activity }: FakeCheckboxProps) {
  const checked = formConfig.watch(activity.name);

  return (
    <label className="fake-checkbox">
      <input
        type="checkbox"
        name={activity.name}
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          formConfig.setValue(activity.name, !checked);
        }}
      />
      <div className="fake-checkbox__content">
        <img src={activity.cover} alt="activity" />
        <span className="fake-checkbox__content-label">{activity.label}</span>
        <div className="fake-checkbox__info-bulle">
          <PopperButton
            openOnHover={false}
            withArrow
            renderButton={(props) => (
              <InfoBulleButton
                size="large"
                {...props}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  props.onClick?.();
                }}
              >
                {" "}
              </InfoBulleButton>
            )}
            renderPopperContent={({ setVisible }) => (
              <InfoBulle
                className="fake-checkbox__info-bulle-size"
                onCancel={() => setVisible(false)}
              >
                <h1>
                  Exemples de&nbsp;
                  <Emphasis>séquelles physiques et psychiques</Emphasis>&nbsp;
                  faisant suite à un accident de la vie courante*
                </h1>
                <h2 className="fake-checkbox__info-bulle-text">
                  {activity.label}
                </h2>
                <div className="fake-checkbox__info-bulle-content">
                  <div>
                    <div className="fake-checkbox__info-bulle-title">
                      <img src="/activities/popover/accident.png" alt="" />
                      <p>Accident</p>
                    </div>
                    {activity.modal.accident}
                  </div>
                  <div>
                    <div className="fake-checkbox__info-bulle-title">
                      <img src="/activities/popover/blessure.png" alt="" />
                      <p>Blessure</p>
                    </div>
                    {activity.modal.blessure}
                  </div>
                  <div>
                    <div className="fake-checkbox__info-bulle-title">
                      <img src="/activities/popover/indemnisation.png" alt="" />
                      <p>Indemnisation</p>
                    </div>
                    {activity.modal.indemnisation}
                  </div>
                </div>
                <small>
                  * Prise en charge dès 1 % d&apos;AIPP (Atteinte à
                  l&apos;Intégrité Physique et Psychique consolidée)
                </small>
              </InfoBulle>
            )}
          />
        </div>
      </div>
    </label>
  );
}

export function Activities({ data, changeStep, setData }: ActivitiesProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: data,
  });

  return (
    <div className="activities-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom ">
          <NavButton onClick={() => changeStep("BIRTHDATE", "backward")}>
            Précédent
          </NavButton>
          <h1>
            Quelles <Emphasis>activités</Emphasis> pratiquez-vous fréquemment ?
          </h1>
          <Form
            formConfig={formConfig}
            onSubmit={(submittedData) => {
              setData(submittedData);
              changeStep("TARIF", "forward");
            }}
          >
            <div className="activities-step__wrapper">
              <FormRow>
                <div className="activities-step__card-grid">
                  {ACTIVITES.map((activity: _Activity) => (
                    <FakeCheckbox
                      key={activity.name}
                      formConfig={formConfig}
                      activity={activity}
                    />
                  ))}
                </div>
              </FormRow>
              <FormRow>
                <FormAction>
                  <Button
                    type="submit"
                    disabled={
                      formConfig.formState.isSubmitting ||
                      formConfig.formState.isSubmitSuccessful
                    }
                  >
                    Suivant
                  </Button>
                </FormAction>
              </FormRow>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
